//
//  Vatom Inc. Copyright (c) 2023, all rights reserved.
//
//  Licensed under the VatomInc SDK License (the "License"); you may not use this file or
//  the Wallet SDK except in compliance with the License accompanying it. Unless
//  required by applicable law or agreed to in writing, the VatomInc SDK distributed under
//  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
//  ANY KIND, either express or implied. See the License for the specific language
//  governing permissions and limitations under the License.
//
import jwtDecode from 'jwt-decode';
export class VatomIdentitySDKFrame {
    constructor(elem, clientId = '', onLogin = () => null, config = {}) {
        this.onMessage = (e) => {
            if (e.data.type === 'authed' && this.onLogin)
                this.onLogin(e.data);
        };
        // Setup message listener
        window.addEventListener('message', this.onMessage);
        const defaultConfig = this.getDefaultConfig();
        // Store vars
        this.clientId = clientId;
        this.onLogin = onLogin;
        this.authority = config.authority || defaultConfig.authority;
        const iframe = document.createElement('iframe');
        let messageSent = false;
        iframe.src = `${this.authority}/shim`;
        iframe.style.height = '100%';
        iframe.style.width = '100%';
        iframe.style.border = 'none';
        elem.replaceChildren(iframe);
        iframe.onload = () => {
            // TODO: if we need to pass an initial message
            if (!messageSent) {
                this.sendMessage({
                    type: 'start',
                    authority: config.authority || defaultConfig.authority,
                    clientId,
                    extraAuthorizeParams: {
                        ...(config.extraAuthorizeParams || defaultConfig.extraAuthorizeParams),
                        layout: "identity-sdk-embedded",
                    },
                });
                messageSent = true;
            }
        };
        this.hostWindow = iframe?.contentWindow || window.opener || window.parent;
    }
    sendMessage(payload) {
        // Send message to parent frame
        // TODO: Figure out if this is a security risk to use the "*" origin. It would allow anyone to embed the viewer inside
        // an iframe and then extract the user's refresh token after they login... Maybe this should instead be controlled
        // through a a list of whitelisted origins.
        if (this.hostWindow && this.hostWindow != window)
            return this.hostWindow.postMessage(payload, '*');
        // Not in a host app
        // throw new Error("Not inside a host app.");
    }
    getDefaultConfig() {
        return { authority: 'https://id.vatom.com', extraAuthorizeParams: {} };
    }
}
const sha256btoaurl = async (code) => {
    const buffer = await self.crypto.subtle.digest('SHA-256', 
    /* @ts-ignore */
    new TextEncoder('utf-8').encode(code));
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    // base64url - slightly modified base64 encoding standard.
    // https://base64.guru/standards/base64url
    return b64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
};
let wellKnownConfig;
const getWellKnownConfig = async (authority) => {
    if (wellKnownConfig)
        return wellKnownConfig;
    const res = await fetch(`${authority}/.well-known/openid-configuration`);
    if (!res.ok)
        throw new Error('could not get well-known config');
    wellKnownConfig = await res.json();
    return wellKnownConfig;
};
const authCodeGrant = async (code, state) => {
    // https://www.rfc-editor.org/rfc/rfc6749#section-4.1.3
    const stateKey = `oidc.${state}`;
    const store = localStorage.getItem(stateKey);
    localStorage.removeItem(stateKey);
    if (!store)
        return false;
    const stateData = JSON.parse(store);
    if (!stateData)
        return false;
    const config = await getWellKnownConfig(stateData.authority);
    const res = await fetch(config.token_endpoint, {
        method: 'POST',
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            code,
            code_verifier: stateData.code_verifier,
            redirect_uri: stateData.redirect_uri,
            client_id: stateData.client_id
        })
    });
    if (!res.ok)
        throw new Error('authorization_code grant res not ok');
    return await res.json();
};
export class VatomIdentitySDK {
    constructor(clientId, config) {
        this._defaultConfig = {
            loginCallbackUri: '/callback',
            logoutCallbackUri: '/logout-callback',
            authority: 'https://id.vatom.com',
            usersUrlBase: 'https://users.vatom.com',
        };
        this.logout = async () => {
            const authority = this._config?.authority ?? this._defaultConfig.authority;
            const config = await getWellKnownConfig(authority);
            const token = this.getStoredTokenResponse();
            if (!token)
                return false;
            const redirectUri = `${window.location.protocol + '//' + window.location.host}${this._config?.logoutCallbackUri ?? this._defaultConfig.logoutCallbackUri}`;
            const url = new URL(config.end_session_endpoint);
            url.searchParams.set('id_token_hint', token.id_token);
            url.searchParams.set('post_logout_redirect_uri', redirectUri);
            location.href = url.toString();
            return;
        };
        this.login = async () => {
            try {
                const state = self.crypto.randomUUID();
                const codeVerifier = self.crypto.randomUUID() + self.crypto.randomUUID() + self.crypto.randomUUID();
                const codeChallenge = await sha256btoaurl(codeVerifier);
                const authority = this._config?.authority ?? this._defaultConfig.authority;
                const scope = 'openid email profile offline_access';
                const config = await getWellKnownConfig(authority);
                const redirectUri = `${window.location.protocol + '//' + window.location.host}${this._config?.loginCallbackUri ?? this._defaultConfig.loginCallbackUri}`;
                // remove old state entries
                for (const key in localStorage) {
                    if (key.startsWith('oidc.'))
                        localStorage.removeItem(key);
                }
                // same format as in "npm:oidc-client"
                localStorage.setItem(`oidc.${state}`, JSON.stringify({
                    id: state,
                    created: Math.floor(Date.now() / 1000),
                    request_type: 'si:r',
                    code_verifier: codeVerifier,
                    redirect_uri: redirectUri,
                    authority,
                    client_id: this._clientId,
                    response_mode: 'query',
                    scope,
                    extraTokenParams: {}
                }));
                const url = new URL(config.authorization_endpoint);
                url.searchParams.set('client_id', this._clientId);
                url.searchParams.set('redirect_uri', redirectUri);
                url.searchParams.set('response_type', 'code');
                url.searchParams.set('scope', scope);
                url.searchParams.set('state', state);
                url.searchParams.set('code_challenge', codeChallenge);
                url.searchParams.set('code_challenge_method', 'S256');
                url.searchParams.set('prompt', 'consent');
                url.searchParams.set('response_mode', 'query');
                if (this._config?.businessId)
                    url.searchParams.set('business-id', this._config?.businessId);
                if (this._config?.anonymousId)
                    url.searchParams.set('anonymous-id', this._config?.anonymousId);
                location.href = url.toString();
            }
            catch (error) {
                console.error('[VatomIdentitySDK] Error: ', error);
            }
        };
        this.onCallbacks = async () => {
            if (window.location.href.includes(this._config?.loginCallbackUri ?? this._defaultConfig.loginCallbackUri)) {
                const q = new URLSearchParams(window.location.search);
                const code = q.get('code');
                const state = q.get('state');
                if (!state || !code)
                    return;
                const res = await authCodeGrant(code, state);
                if (res)
                    this.storeTokenResponse(res);
                return true;
            }
            if (window.location.href.includes(this._config?.logoutCallbackUri ?? this._defaultConfig.logoutCallbackUri)) {
                this.storeTokenResponse(null);
                return true;
            }
            return false;
        };
        this.getAccessToken = async () => {
            let store = this.getStoredTokenResponse();
            if (!store)
                return null;
            if (store.expires_at * 1000 <= Date.now()) {
                try {
                    store = await this.refreshToken();
                }
                catch (e) {
                    return null;
                }
            }
            return store.access_token;
        };
        this.getIsAuthed = async () => {
            return !!(await this.getAccessToken());
        };
        this.getProfile = async () => {
            const authority = this._config?.authority ?? this._defaultConfig.authority;
            const res = await fetch(`${authority}/api/me`, {
                headers: { authorization: `Bearer ${await this.getAccessToken()}` }
            });
            if (!res.ok)
                throw new Error(`get profile failed: ${res.status}`);
            return await res.json();
        };
        this.getIsProfileComplete = async (businessId) => {
            const authority = this._config?.authority ?? this._defaultConfig.authority;
            const res = await fetch(`${authority}/api/me/info/form?businessId=${businessId}`, {
                headers: { authorization: `Bearer ${await this.getAccessToken()}` }
            });
            if (!res.ok)
                throw new Error(`get isProfileComplete failed: ${res.status}`);
            return (await res.json()).length === 0;
        };
        this.loginGuest = async () => {
            const authority = this._config?.authority ?? this._defaultConfig.authority;
            const config = await getWellKnownConfig(authority);
            const res = await fetch(config.token_endpoint, {
                method: 'POST',
                headers: {
                    'content-type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams({
                    grant_type: 'urn:vatominc:params:oauth:grant-type:guest-registration',
                    client_id: this._clientId
                })
            });
            if (!res.ok)
                throw new Error(`guest registration failed: ${res.status}`);
            this.storeTokenResponse(await res.json());
        };
        this.refreshToken = async () => {
            const authority = this._config?.authority ?? this._defaultConfig.authority;
            const config = await getWellKnownConfig(authority);
            const token = this.getStoredTokenResponse();
            if (!token)
                throw new Error('no token to refresh');
            const res = await fetch(config.token_endpoint, {
                method: 'POST',
                headers: {
                    'content-type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams({
                    grant_type: 'refresh_token',
                    client_id: this._clientId,
                    refresh_token: token.refresh_token
                })
            });
            if (!res.ok)
                throw new Error(`token refresh failed: ${res.status}`);
            const resData = await res.json();
            this.storeTokenResponse(resData);
            return resData;
        };
        this.getStoredTokenResponse = () => {
            const str = localStorage.getItem('VATOM_IDENTITY_SDK_TOKEN');
            if (!str)
                return null;
            return JSON.parse(str);
        };
        this.storeTokenResponse = (res) => {
            const key = 'VATOM_IDENTITY_SDK_TOKEN';
            if (res) {
                const store = Object.assign({}, res);
                store.expires_at = Date.now() / 1000 + store.expires_in;
                localStorage.setItem(key, JSON.stringify(store));
            }
            else {
                localStorage.removeItem(key);
                return;
            }
        };
        this.getPublicUserProfile = async (userId) => {
            const decodedToken = jwtDecode(await this.getAccessToken());
            const usersUrlBase = this._config?.usersUrlBase ?? this._defaultConfig.usersUrlBase;
            try {
                const response = await fetch(`${usersUrlBase}/${userId || decodedToken.sub}`);
                if (response.status !== 200) {
                    return undefined;
                }
                const resp = await response.json();
                // If the user is undefined, the response will be {}
                const id = resp.id || resp.sub;
                if (!id)
                    return undefined;
                return {
                    ...resp,
                    id
                };
            }
            catch (err) {
                return undefined;
            }
        };
        this.isLoggedIn = async () => {
            try {
                return await this.refreshToken();
            }
            catch (e) {
                return false;
            }
        };
        this.getIdentities = async () => {
            const response = await fetch(`${this._config.authority || this._defaultConfig.authority}/me/identities`, {
                headers: {
                    Authorization: `Bearer ${await this.getAccessToken()}`
                }
            });
            if (response.status !== 200) {
                return undefined;
            }
            const resp = await response.json();
            return resp;
        };
        this._state = self.crypto.randomUUID();
        this._config = config ?? {};
        this._clientId = clientId;
    }
}
//# sourceMappingURL=index.js.map